import moment from 'moment'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import Form from 'react-bootstrap/Form'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import filterSolidIcon from '../../assets/images/filter-solid-icon.svg'
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle'
import SelectCustomOption from '../../components/SelectCustomOption'
import { GL_VP, MONTHS, SELECT_CUSTOM_STYLE } from '../../resources/constants'
import { BankTransSearchFormI } from '../../resources/form-props'
import { calculateMaxHeightContent, fileDownloadBlob, lastYears } from '../../resources/functions'
import { useChartAccountApi } from '../../resources/hooks/api/chartAccountApiHook'
import { useTransTableDataApi } from '../../resources/hooks/api/transApiHook'
import { useReportApi } from '../../resources/hooks/api/useReportApiHook'
import { getBranch } from '../../store/user/selectors'
import './filter.css'

interface Props {}

type SortField =
  | 'date'
  | 'expense'
  | 'deposit'
  | 'equity'
  | 'bankTransfer'
  | 'others'
  | 'adjustedBankDeposit'
type SortDirection = 'asc' | 'desc'
type FilterPopup = 'posted' | 'coa' | null

interface SortConfig {
  field: SortField | null
  direction: SortDirection | null
}

interface TableRowModel {
  originalIndex: number
  rec: any
  coaValue: string
  postedValue: string
  transactionTimestamp: number
  expenseValue: number
  depositValue: number
  equityValue: number
  bankTransferValue: number
  othersValue: number
  adjustedBankDepositValue: number
}

const parseTransactionDate = (value: any) => {
  if (!value) {
    return 0
  }

  const isoDate = moment.utc(value, moment.ISO_8601, true)

  if (isoDate.isValid()) {
    return isoDate.valueOf()
  }

  const formattedDate = moment.utc(
    value,
    ['MM-DD-YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'],
    true
  )

  if (formattedDate.isValid()) {
    return formattedDate.valueOf()
  }

  const fallbackDate = moment.utc(value)

  return fallbackDate.isValid() ? fallbackDate.valueOf() : 0
}

const parseExpenseValue = (value: any) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  const parsedValue = parseFloat(String(value ?? '').replace(/[^0-9.-]/g, ''))

  return Number.isNaN(parsedValue) ? 0 : parsedValue
}

const amountNumberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const formatCurrencyAmount = (value: any) =>
  `$ ${amountNumberFormatter.format(Math.abs(parseExpenseValue(value)))}`

const HeaderIcon = ({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden="true"
  >
    {children}
  </svg>
)

const SortIndicatorIcon = ({
  direction,
}: {
  direction: SortDirection | null
}) => {
  if (direction === 'asc') {
    return (
      <HeaderIcon className="table-header-icon">
        <path
          fillRule="evenodd"
          d="M8 12a.5.5 0 0 0 .5-.5V3.707l2.146 2.147a.5.5 0 0 0 .708-.708l-3-3a.5.5 0 0 0-.708 0l-3 3a.5.5 0 1 0 .708.708L7.5 3.707V11.5A.5.5 0 0 0 8 12z"
        />
      </HeaderIcon>
    )
  }

  if (direction === 'desc') {
    return (
      <HeaderIcon className="table-header-icon">
        <path
          fillRule="evenodd"
          d="M8 4a.5.5 0 0 0-.5.5v7.793L5.354 10.146a.5.5 0 1 0-.708.708l3 3a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 12.293V4.5A.5.5 0 0 0 8 4z"
        />
      </HeaderIcon>
    )
  }

  return (
    <HeaderIcon className="table-header-icon table-header-icon--muted">
      <path
        fillRule="evenodd"
        d="M11.5 15a.5.5 0 0 0 .5-.5V2.707l1.146 1.147a.5.5 0 0 0 .708-.708l-2-2a.5.5 0 0 0-.708 0l-2 2a.5.5 0 1 0 .708.708L11 2.707V14.5a.5.5 0 0 0 .5.5zm-7-14a.5.5 0 0 0-.5.5v11.793L2.854 12.146a.5.5 0 1 0-.708.708l2 2a.5.5 0 0 0 .708 0l2-2a.5.5 0 1 0-.708-.708L5 13.293V1.5a.5.5 0 0 0-.5-.5z"
      />
    </HeaderIcon>
  )
}

const FilterIndicatorIcon = ({ active }: { active: boolean }) => (
  <HeaderIcon
    className={`table-header-icon${
      active ? ' table-header-icon--active' : ' table-header-icon--muted'
    }`}
  >
    <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .39.812L10 7.333V12.5a.5.5 0 0 1-.223.416l-2 1.5A.5.5 0 0 1 7 14V7.333L1.61 1.812A.5.5 0 0 1 1.5 1.5z" />
  </HeaderIcon>
)

const BankTransaction: React.FC<Props> = () => {
  const { t } = useTranslation('scenes.transactions.bankTransactions');
  const {  downloadBTReport } = useReportApi();
  const { fetchBankTransData } = useTransTableDataApi()
  
  const [record, setRecords] = useState<any>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [revenueData, setRevenueData] = useState<any>([])
  const [expenseData, setExpenseData] = useState<any>([])
  const [data, setData] = useState<any>([])
  const [filterData, setFilterData] = useState<any>({
    year: 2024,
    month: 1,
    trans_type: 1,
    account: null,
    date_range: null,
  })
  const [transationType, setTransationType] = useState<any>(1)
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })
  const [minDate, setMinDate] = useState(new Date(filterData.year, filterData.month - 1, 1));
  const [maxDate, setMaxDate] = useState(new Date(filterData.year, filterData.month, 0));

  const {
    control: control2,
    watch,
    handleSubmit: handleSubmit2,
    formState: { errors: errors2 },
    setValue,
  } = useForm<BankTransSearchFormI>({
    defaultValues: {
      account: null,
      dateRange: { startDate: null, endDate: null },
      month: {
        value: '01',
        label:  t('month.01', { ns: 'resources.form-validator' }),
      },
      year: { value: '2024', label: '2024' },
      trans_type: { value: '1', label: t('option.withdrawal') },
    },
    //resolver: yupResolver(GLSearchFormValidate)
  })

 
  // Start Paggination
  const [currentPage, setCurrentPage] = useState<any>(1)

  // const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage] = useState<any>(5000) // Change this value as needed
  const [totalPages, setTotalPages] = useState<any>(1)

  const [firstIndex, setFirstIndex] = useState<any>(1)
  const [lastIndex, setLastIndex] = useState<any>(1)

  const { glAccountsList } = useChartAccountApi()

  const [totalDeposit, setTotalDeposit] = useState<any>(0)
  const [totalEquity, setTotalEquity] = useState<any>(0)
  const [totalBankTransfer, setTotalBankTransfer] = useState<any>(0)
  const [totalOthers, setTotalOthers] = useState<any>(0)
  const [totalAdjustedBankDeposit, setTotalAdjustedBankDeposit] = useState<any>(
    0
  )
  const [totalExpense, setTotalExpense] = useState<any>(0)
  const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
  const [selectCustomStyle, setSlectCustomStyle] = useState<any>(
    SELECT_CUSTOM_STYLE('#fff')
  )
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: null,
    direction: null,
  })
  const [activeFilterPopup, setActiveFilterPopup] = useState<FilterPopup>(null)
  const [postedFilterValues, setPostedFilterValues] = useState<string[]>([])
  const [coaFilterValues, setCoaFilterValues] = useState<string[]>([])
  const postedFilterRef = useRef<HTMLDivElement | null>(null)
  const coaFilterRef = useRef<HTMLDivElement | null>(null)
const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const branchData = useSelector(getBranch)

  const selectedYear = watch('year', ''); 
  const selectedMonth = watch('month', ''); 
  const emptyCoaLabel = t('common.na')

  useEffect(() => {
    fetchTransactionList()
  }, [filterData])

  useEffect(() => {
    setContentMaxHeight(calculateMaxHeightContent(78))
  })


  useEffect(() => {
    setMinDate(new Date(selectedYear.value, selectedMonth.value - 1, 1));
    setMaxDate(new Date(selectedYear.value, selectedMonth.value, 0));
  }, [selectedYear,selectedMonth])

  useEffect(() => {
  const clearedDateRange = {
    startDate: null,
    endDate: null,
  }

  setDateRange(clearedDateRange)
  setValue('dateRange', clearedDateRange)
 }, [minDate, maxDate, setValue])

  useEffect(() => {
    if (!activeFilterPopup) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (
        activeFilterPopup === 'posted' &&
        postedFilterRef.current &&
        !postedFilterRef.current.contains(target)
      ) {
        setActiveFilterPopup(null)
      }

      if (
        activeFilterPopup === 'coa' &&
        coaFilterRef.current &&
        !coaFilterRef.current.contains(target)
      ) {
        setActiveFilterPopup(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [activeFilterPopup])

  useEffect(() => {
    setActiveFilterPopup(null)
  }, [data, transationType])

  useEffect(() => {
    const availableSortFields: SortField[] =
      transationType === 3
        ? [
            'date',
            'deposit',
            'equity',
            'bankTransfer',
            'others',
            'adjustedBankDeposit',
          ]
        : ['date', 'expense']

    if (sortConfig.field && !availableSortFields.includes(sortConfig.field)) {
      setSortConfig({
        field: null,
        direction: null,
      })
    }
  }, [sortConfig.field, transationType])

  const fetchTransactionList = () => {
    const params: any = {
      page: currentPage,
      per_page: recordPerPage,
      year: +filterData['year'],
      month: +filterData['month'],
      account_type: +filterData['trans_type'],
      branch_id: +branchData['id'],
    }

    if (filterData['account']) {
      params.account = +filterData['account']
    }

    if (filterData['dateRange'] && filterData['dateRange'].startDate) {
      params.start_day = moment(filterData['dateRange'].startDate).format('D')
    }

    if (filterData['dateRange'] && filterData['dateRange'].endDate) {
      params.end_day = moment(filterData['dateRange'].endDate).format('D')
    }

    let tempTotalDeposit = 0
    let tempTotalEquity = 0
    let tempTotalBankTransfer = 0
    let tempTotalOthers = 0
    let tempTotalAdjustedBankDeposit = 0
    let tempTotalExpense = 0

    setIsLoading(true)

    fetchBankTransData(
      params,
      (message: string, resp: any) => {
        const result = resp.data.data.result

        // Set pagination values
        setFirstIndex(resp.data.data.page)
        setLastIndex(resp.data.data.total)
        setTotalPages(resp.data.data.total)
        const push_rec: any[] = []

        result.forEach((rec: any, index: any) => {
          if (rec.account_type == 3) {
            const depositData = rec['breakdowns'].filter(
              (e: any) => e['account_type'] == 'Deposit'
            )
            const equityData = rec['breakdowns'].filter(
              (e: any) => e['account_type'] == 'Equity'
            )
            const bankTransferData = rec['breakdowns'].filter(
              (e: any) => e['account_type'] == 'Bank_Transfer'
            )
            const othersData = rec['breakdowns'].filter(
              (e: any) => e['account_type'] == 'Others'
            )
            const abdData = rec['breakdowns'].filter(
              (e: any) => e['account_type'] == 'Adjusted_Bank_Deposit'
            )

            const rec1 = {
              parent_id: rec.parent_id,
              account_type: rec.account_type,
              description: rec.description,
              account_name: rec.account_name,
              transaction_at: rec.transaction_at,
              deposit: depositData.reduce((a: any, c: any) => a + c['account_value'], 0),
                // (depositData.length && depositData[0]['account_value']) || 0,
              equity: equityData.reduce((a: any, c: any) => a + c['account_value'], 0),
                // (equityData.length && equityData[0]['account_value']) || 0,
              bank_transfer: bankTransferData.reduce((a: any, c: any) => a + c['account_value'], 0),
                // (bankTransferData.length && bankTransferData[0]['account_value']) || 0,
              others: othersData.reduce((a: any, c: any) => a + c['account_value'], 0),
              // (othersData.length && othersData[0]['account_value']) || 0,
              adjusted_bank_deposit: abdData.reduce((a: any, c: any) => a + c['account_value'], 0),
                // (abdData.length && abdData[0]['account_value']) || 0,
              posted: rec.updated_at ? 'YES' : 'NO',
              account:
                (depositData.length &&
                  depositData[0]['account_chart'] &&
                  depositData[0]['account_chart'].account) ||
                'NA',
            }
            tempTotalDeposit += Math.abs(rec1.deposit)
            tempTotalEquity += Math.abs(rec1.equity)
            tempTotalBankTransfer += Math.abs(rec1.bank_transfer)
            tempTotalOthers += Math.abs(rec1.others)
            tempTotalAdjustedBankDeposit += Math.abs(rec1.adjusted_bank_deposit)

            push_rec.push(rec1)

            // totalExpense += transationType === 1 ? Math.abs(rec.expense) : 0;
          } else {
            const expenseData = rec['breakdowns'].filter(
              (e: any) => e['account_type'] == 'Credit_Deposit'
            )
            const rec2 = {
              parent_id: rec.parent_id,
              account_type: rec.account_type,
              description: rec.description,
              account_name: rec.account_name,
              transaction_at: rec.transaction_at,
              expense:
                (expenseData.length && expenseData[0]['account_value']) || 0,
              account:
                (expenseData.length &&
                  expenseData[0]['account_chart'] &&
                  expenseData[0]['account_chart'].account) ||
                'NA',
              posted: rec.updated_at ? 'YES' : 'NO',
            }
            tempTotalExpense += Math.abs(rec2.expense)

            push_rec.push(rec2)
          }

          //storeData.push(main_rec);
        })

        setData(push_rec)

        // Update totals

        setTotalDeposit(tempTotalDeposit)
        setTotalEquity(tempTotalEquity)
        setTotalBankTransfer(tempTotalBankTransfer)
        setTotalOthers(tempTotalOthers)
        setTotalAdjustedBankDeposit(tempTotalAdjustedBankDeposit)
        setTotalExpense(tempTotalExpense)

        setIsLoading(false)
      },
      (message: string, resp: any) => {
        setIsLoading(false)
        toast.error(message)
      }
    )
  }
  const processSearchData = (data: any) => {
    setFilterData({
      year: +data['year']['value'],
      month: +data['month']['value'],
      trans_type: +data['trans_type']['value'],
      account: data['account'] ? +data['account']['account'] : null,
      dateRange: data['dateRange'],
    })
    setTransationType(+data['trans_type']['value'])
  }

  const fetchAccountList = (searchText: string = ''): Promise<any[]> => {
    return new Promise<any>((resolve, reject) => {
      const params: any = {
        page: 1,
        per_page: 10,
        branch_id: +branchData['id'],
      }
      if (searchText) {
        params['name'] = searchText
      }
      params['parent_account_in'] = GL_VP.BANK_ACCOUNT.account.join(',')
      params['include_assigned_vp'] = 1

      glAccountsList(
        params,
        (message: string, resp: any) => {
          const data = resp.data.data.result.map((e: any) => ({
            label: e['name'] + `(${e['account']})`,
            value: e['id'],
            account: e['account'],
          }))
          return resolve(data)
        },
        (message: string, resp: any) => {
          toast.error(message)
          return resolve([])
        }
      )
    })
  }

  const filterAccountList = async (inputValue: string) => {
    const data = await fetchAccountList(inputValue)
    return data
  }

  const handleDateChange = (dates: any) => {
    const [start, end] = dates

    const updatedDateRange = {
      startDate: start,
      endDate: end,
    }

    setDateRange(updatedDateRange)
    setValue('dateRange', updatedDateRange)
  }

  const handleClearDateRange = () => {
    const clearedDateRange = {
      startDate: null,
      endDate: null,
    }

    setDateRange(clearedDateRange)
    setValue('dateRange', clearedDateRange)

    setFilterData((prev: any) => ({
      ...prev,
      dateRange: clearedDateRange,
    }))
  }

  const _downloadReport = () => {
    const params: any = {
      page: currentPage,
      per_page: recordPerPage,
      year: +filterData['year'],
      month: +filterData['month'],
      account_type: +filterData['trans_type'],
      branch_id: +branchData['id'],
    }

    if (filterData['account']) {
      params.account = +filterData['account']
    }

    if (filterData['dateRange'] && filterData['dateRange'].startDate) {
      params.start_day = moment(filterData['dateRange'].startDate).format('D')
    }

    if (filterData['dateRange'] && filterData['dateRange'].endDate) {
      params.end_day = moment(filterData['dateRange'].endDate).format('D')
    }

    setIsLoading(true);
    downloadBTReport(params, (message: string, resp: any) => {
          setIsLoading(false);
          fileDownloadBlob(resp, `bank-transaction-${filterData.year}-${filterData.month}`)
    }, (message: string, resp: any) => {
          setIsLoading(false);
          toast.error(message);
    })
  }
  const tableRows = useMemo<TableRowModel[]>(
    () =>
      data.map((rec: any, index: number) => ({
        originalIndex: index,
        rec,
        coaValue:
          rec.account === null || rec.account === undefined || `${rec.account}`.trim() === ''
            ? emptyCoaLabel
            : `${rec.account}`.trim(),
        postedValue:
          `${rec.posted ?? ''}`.trim().toUpperCase() === 'YES' ? 'YES' : 'NO',
        transactionTimestamp: parseTransactionDate(rec.transaction_at),
        expenseValue: Math.abs(parseExpenseValue(rec.expense)),
        depositValue: Math.abs(parseExpenseValue(rec.deposit)),
        equityValue: Math.abs(parseExpenseValue(rec.equity)),
        bankTransferValue: Math.abs(parseExpenseValue(rec.bank_transfer)),
        othersValue: Math.abs(parseExpenseValue(rec.others)),
        adjustedBankDepositValue: Math.abs(
          parseExpenseValue(rec.adjusted_bank_deposit)
        ),
      })),
    [data, emptyCoaLabel]
  )

  const uniqueCoaValues = useMemo(
    () => Array.from(new Set(tableRows.map((row) => row.coaValue))),
    [tableRows]
  )
  const uniquePostedValues = useMemo(
    () => Array.from(new Set(tableRows.map((row) => row.postedValue))),
    [tableRows]
  )

  const hasMultipleCoaValues = uniqueCoaValues.length > 1
  const hasMultiplePostedValues = uniquePostedValues.length > 1
  const isPostedFilterActive =
    hasMultiplePostedValues &&
    postedFilterValues.length > 0 &&
    postedFilterValues.length < uniquePostedValues.length
  const isCoaFilterActive =
    hasMultipleCoaValues &&
    coaFilterValues.length > 0 &&
    coaFilterValues.length < uniqueCoaValues.length

  useEffect(() => {
    setCoaFilterValues((previousValues) => {
      if (uniqueCoaValues.length <= 1) {
        return previousValues.length ? [] : previousValues
      }

      const nextValues = previousValues.filter((value) =>
        uniqueCoaValues.includes(value)
      )

      return nextValues.length === previousValues.length
        ? previousValues
        : nextValues
    })

    if (uniqueCoaValues.length <= 1 && activeFilterPopup === 'coa') {
      setActiveFilterPopup(null)
    }
  }, [activeFilterPopup, uniqueCoaValues])

  useEffect(() => {
    if (hasMultiplePostedValues) {
      return
    }

    setPostedFilterValues((previousValues) =>
      previousValues.length ? [] : previousValues
    )

    if (activeFilterPopup === 'posted') {
      setActiveFilterPopup(null)
    }
  }, [activeFilterPopup, hasMultiplePostedValues])

  const displayedRows = useMemo(() => {
    let nextRows = tableRows

    if (postedFilterValues.length > 0) {
      nextRows = nextRows.filter((row) =>
        postedFilterValues.includes(row.postedValue)
      )
    }

    if (coaFilterValues.length > 0) {
      nextRows = nextRows.filter((row) => coaFilterValues.includes(row.coaValue))
    }

    if (!sortConfig.field || !sortConfig.direction) {
      return nextRows
    }

    const sortedRows = [...nextRows].sort((left, right) => {
      const getSortValue = (row: TableRowModel) => {
        switch (sortConfig.field) {
          case 'date':
            return row.transactionTimestamp
          case 'expense':
            return row.expenseValue
          case 'deposit':
            return row.depositValue
          case 'equity':
            return row.equityValue
          case 'bankTransfer':
            return row.bankTransferValue
          case 'others':
            return row.othersValue
          case 'adjustedBankDeposit':
            return row.adjustedBankDepositValue
          default:
            return row.originalIndex
        }
      }

      const leftValue = getSortValue(left)
      const rightValue = getSortValue(right)

      if (leftValue === rightValue) {
        return left.originalIndex - right.originalIndex
      }

      return sortConfig.direction === 'asc'
        ? leftValue - rightValue
        : rightValue - leftValue
    })

    return sortedRows
  }, [coaFilterValues, postedFilterValues, sortConfig, tableRows])

  const displayTotals = useMemo(
    () =>
      displayedRows.reduce(
        (totals, row) => {
          totals.deposit += Math.abs(parseExpenseValue(row.rec.deposit))
          totals.equity += Math.abs(parseExpenseValue(row.rec.equity))
          totals.bankTransfer += Math.abs(parseExpenseValue(row.rec.bank_transfer))
          totals.others += Math.abs(parseExpenseValue(row.rec.others))
          totals.adjustedBankDeposit += Math.abs(
            parseExpenseValue(row.rec.adjusted_bank_deposit)
          )
          totals.expense += Math.abs(parseExpenseValue(row.rec.expense))

          return totals
        },
        {
          deposit: 0,
          equity: 0,
          bankTransfer: 0,
          others: 0,
          adjustedBankDeposit: 0,
          expense: 0,
        }
      ),
    [displayedRows]
  )

  const toggleSort = (field: SortField) => {
    setSortConfig((previousSort) => {
      if (field === 'date') {
        if (!previousSort.field && !previousSort.direction) {
          return {
            field,
            direction: 'desc',
          }
        }

        if (previousSort.field === 'date') {
          return {
            field,
            direction: previousSort.direction === 'desc' ? 'asc' : 'desc',
          }
        }

        return {
          field,
          direction: 'asc',
        }
      }

      if (previousSort.field !== field) {
        return {
          field,
          direction: 'asc',
        }
      }

      if (previousSort.direction === 'asc') {
        return {
          field,
          direction: 'desc',
        }
      }

      return {
        field: null,
        direction: null,
      }
    })
  }

  const getSortDirection = (field: SortField) =>
    field === 'date' && !sortConfig.field && !sortConfig.direction
      ? 'asc'
      : sortConfig.field === field
      ? sortConfig.direction
      : null

  const toggleFilterPopup = (popup: Exclude<FilterPopup, null>) => {
    setActiveFilterPopup((currentPopup) =>
      currentPopup === popup ? null : popup
    )
  }

  const togglePostedFilter = (value: string) => {
    setPostedFilterValues((previousValues) =>
      previousValues.includes(value)
        ? previousValues.filter((item) => item !== value)
        : [...previousValues, value]
    )
  }

  const toggleCoaFilter = (value: string) => {
    setCoaFilterValues((previousValues) =>
      previousValues.includes(value)
        ? previousValues.filter((item) => item !== value)
        : [...previousValues, value]
    )
  }

  const resetPostedFilter = () => {
    setPostedFilterValues([])
    setActiveFilterPopup(null)
  }

  const resetCoaFilter = () => {
    setCoaFilterValues([])
    setActiveFilterPopup(null)
  }

  return (
    <div className="main-container flex-grow-1">
      <div className="container-fluid">
         <div className="d-flex justify-content-between align-items-center flex-wrap pb-3 pt-2" id="ux_page_title">
           <div >
              <h1 className="h5 font-weight-700 mb-0 d-inline-flex text-black">
                {t('title')}
              </h1>
            </div>
             <div className="d-flex align-items-center gap-2 flex-wrap">
                 <div className="table-top" id="ux_page_filter">
              <div className="row">
                <Form>
                  <div className="col-sm-12">
                    <div className="filter-wrap">
                      <Form.Group
                        className="filter-box"
                        controlId="formBasicEmail"
                      >
                        <Controller
                          name="month"
                          control={control2}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={MONTHS}
                              className={'zindex-10'}
                            />
                          )}
                        />
                        {(errors2 &&
                          errors2['month'] &&
                          errors2['month'].message && (
                            <Form.Text className="text-danger">
                              {(errors2['month'].message as ReactNode) || ''}
                            </Form.Text>
                          )) ||
                          ''}
                      </Form.Group>
                      <Form.Group
                        className="filter-box"
                        controlId="formBasicEmail"
                      >
                        <Controller
                          name="year"
                          control={control2}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={lastYears(5)}
                              className={'zindex-10'}
                            />
                          )}
                        />
                        {(errors2 &&
                          errors2['year'] &&
                          errors2['year'].message && (
                            <Form.Text className="text-danger">
                              {(errors2['year'].message as ReactNode) || ''}
                            </Form.Text>
                          )) ||
                          ''}
                      </Form.Group>

                      <Form.Group
                        className="filter-box"
                        controlId="formBasicEmail"
                      >
                        <Controller
                          name="trans_type"
                          control={control2}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={[
                                { value: '1', label: t('option.withdrawal') },
                                { value: '3', label: t('option.deposit') },
                              ]}
                              className={'zindex-10'}
                            />
                          )}
                        />
                        {(errors2 &&
                          errors2['trans_type'] &&
                          errors2['trans_type'].message && (
                            <Form.Text className="text-danger">
                              {(errors2['trans_type'].message as ReactNode) ||
                                ''}
                            </Form.Text>
                          )) ||
                          ''}
                      </Form.Group>

                      <Form.Group
                        className="filter-box"
                        controlId="formBasicEmail"
                      >
                        <Controller
                          name="account"
                          control={control2}
                          render={({ field }) => (
                            <AsyncSelect
                              {...field}
                              isClearable={true}
                              placeholder={t('placeholder.selectBank')}
                              defaultOptions
                              loadOptions={filterAccountList}
                              className={'zindex-10'}
                              menuPosition="fixed"
                              styles={selectCustomStyle}
                              components={{ Option: SelectCustomOption }}
                              // isDisabled={isSelecteBank}
                            />
                          )}
                        />
                      </Form.Group>

                    <Form.Group
                      className="filter-box"
                      controlId="formBasicEmail"
                    >
                      <Controller
                        name="dateRange"
                        control={control2}
                        render={({ field }) => (
                          <div className="date-range-wrapper">
                            <DatePicker
                              {...field}
                              selected={dateRange.startDate}
                              onChange={(date) => handleDateChange(date)}
                              selectsRange
                              startDate={dateRange.startDate}
                              endDate={dateRange.endDate}
                              minDate={minDate}
                              maxDate={maxDate}
                              
                              className="form-control datepicker-transaction date-range-input bg-white"
                              placeholderText={t('placeholder.selectDateRange')}
                            />

                            {dateRange.startDate && dateRange.endDate ? (
                              <button
                                type="button"
                                className="date-range-clear-btn btn-close"
                                onClick={handleClearDateRange}
                                aria-label="Clear date range"
                              />
                            ) : null}
                          </div>
                        )}
                      />
                    </Form.Group>

                      <div className="filter-btn" style={{ marginRight: '8px' }}>
                        <button
                          type="button"
                          className="button"
                          onClick={handleSubmit2(processSearchData)}
                        >
                          <img src={filterSolidIcon} alt="" /> {t('button.filter')}
                        </button>
                      </div>

                      <div className="filter-btn">
                        <div
                          className={`page-loader ${
                            isLoading ? 'visible' : ''
                          }`}
                        >
                          <div className="loader"></div>
                        </div>
                      </div>

                      {
                                  data.length ? (
                                        <div className="filter-box text-end">
                                            <button type="button" onClick={_downloadReport}  className="btn-dark btn btn-lg">
                                                {t('button.exportPdf')} &nbsp;
                                                <i className="fa fa-download" aria-hidden="true"></i>
                                            </button>
                                        </div>
                                    ) : <></>
                      }

                     
                    </div>
                  </div>

                  
                </Form>
                
              </div>
            </div>
             </div>
         </div>

        <div className=" executive-dashboard">
          <div className="dashboard-top-part">
            <div
                     className="table-responsive list-table table-scroll"
              style={{ maxHeight: 'calc(100vh - 220px)',overflowY: 'auto' }}
              ref={tableContainerRef}
            >
              <table className="table table-default">
                <thead className="zindex-0">
                  <tr className="align-middle">
                    <th className="text-start">{t('table.number')}</th>
                    <th className="text-center">
                      <button
                        type="button"
                        className="table-header-sort-btn table-header-sort-btn--center"
                        onClick={() => toggleSort('date')}
                      >
                        <span>{t('table.date')}</span>
                        <SortIndicatorIcon
                          direction={getSortDirection('date')}
                        />
                      </button>
                    </th>
                    <th className="text-center">
                      <div
                        className="table-header-filter-wrap table-header-filter-wrap--center"
                        ref={coaFilterRef}
                      >
                        <span>{t('table.coaNumber')}</span>
                        {hasMultipleCoaValues ? (
                          <>
                            <button
                              type="button"
                              className={`table-header-icon-btn${
                                isCoaFilterActive ? ' active' : ''
                              }`}
                              onClick={() => toggleFilterPopup('coa')}
                              aria-label={`Filter ${t('table.coaNumber')}`}
                              aria-expanded={activeFilterPopup === 'coa'}
                            >
                              <FilterIndicatorIcon active={isCoaFilterActive} />
                            </button>
                            {activeFilterPopup === 'coa' ? (
                              <div className="table-filter-popup table-filter-popup--coa">
                                <div className="table-filter-popup__actions">
                                  <button
                                    type="button"
                                    className="table-filter-popup__action"
                                    onClick={() =>
                                      setCoaFilterValues([...uniqueCoaValues])
                                    }
                                  >
                                    {t('button.selectAll')}
                                  </button>
                                  <button
                                    type="button"
                                    className="table-filter-popup__action table-filter-popup__action--reset"
                                    onClick={resetCoaFilter}
                                  >
                                    {t('button.reset')}
                                  </button>
                                </div>
                                <div className="table-filter-popup__checklist table-filter-popup__checklist--coa">
                                  {uniqueCoaValues.map((coaValue) => (
                                    <label
                                      key={coaValue}
                                      className="table-filter-option"
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input m-0"
                                        checked={coaFilterValues.includes(coaValue)}
                                        onChange={() => toggleCoaFilter(coaValue)}
                                      />
                                      <span>{coaValue}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </th>
                    <th className="text-start">{t('table.description')}</th>
                    {transationType == 3 ? (
                      <>
                        <th className="text-end">
                          <button
                            type="button"
                            className="table-header-sort-btn table-header-sort-btn--end"
                            onClick={() => toggleSort('deposit')}
                          >
                            <span>{t('table.deposit')}</span>
                            <SortIndicatorIcon
                              direction={getSortDirection('deposit')}
                            />
                          </button>
                        </th>
                        <th className="text-end">
                          <button
                            type="button"
                            className="table-header-sort-btn table-header-sort-btn--end"
                            onClick={() => toggleSort('equity')}
                          >
                            <span>{t('table.equity')}</span>
                            <SortIndicatorIcon
                              direction={getSortDirection('equity')}
                            />
                          </button>
                        </th>
                        <th className="text-end">
                          <button
                            type="button"
                            className="table-header-sort-btn table-header-sort-btn--end"
                            onClick={() => toggleSort('bankTransfer')}
                          >
                            <span>{t('table.bankTransfer')}</span>
                            <SortIndicatorIcon
                              direction={getSortDirection('bankTransfer')}
                            />
                          </button>
                        </th>
                        <th className="text-end">
                          <button
                            type="button"
                            className="table-header-sort-btn table-header-sort-btn--end"
                            onClick={() => toggleSort('others')}
                          >
                            <span>{t('table.other')}</span>
                            <SortIndicatorIcon
                              direction={getSortDirection('others')}
                            />
                          </button>
                        </th>
                        <th className="text-end">
                          <button
                            type="button"
                            className="table-header-sort-btn table-header-sort-btn--end"
                            onClick={() => toggleSort('adjustedBankDeposit')}
                          >
                            <span>{t('table.abd')}</span>
                            <SortIndicatorIcon
                              direction={getSortDirection('adjustedBankDeposit')}
                            />
                          </button>
                        </th>
                      </>
                    ) : transationType == 1 ? (
                      <>
                        <th className="text-end">
                          <button
                            type="button"
                            className="table-header-sort-btn table-header-sort-btn--end"
                            onClick={() => toggleSort('expense')}
                          >
                            <span>{t('table.expense')}</span>
                            <SortIndicatorIcon
                              direction={getSortDirection('expense')}
                            />
                          </button>
                        </th>
                      </>
                    ) : (
                      <></>
                    )}

                    <th className="text-center">
                      <div
                        className="table-header-filter-wrap table-header-filter-wrap--end"
                        ref={postedFilterRef}
                      >
                        <span>{t('table.posted')}</span>
                        {hasMultiplePostedValues ? (
                          <>
                            <button
                              type="button"
                              className={`table-header-icon-btn${
                                isPostedFilterActive ? ' active' : ''
                              }`}
                              onClick={() => toggleFilterPopup('posted')}
                              aria-label={`Filter ${t('table.posted')}`}
                              aria-expanded={activeFilterPopup === 'posted'}
                            >
                              <FilterIndicatorIcon active={isPostedFilterActive} />
                            </button>
                            {activeFilterPopup === 'posted' ? (
                              <div className="table-filter-popup table-filter-popup--posted">
                                <div className="table-filter-popup__checklist">
                                  {[
                                    { label: t('option.yes'), value: 'YES' },
                                    { label: t('option.no'), value: 'NO' },
                                  ].map((option) => (
                                    <label
                                      key={option.value}
                                      className="table-filter-option"
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input m-0"
                                        checked={postedFilterValues.includes(
                                          option.value
                                        )}
                                        onChange={() =>
                                          togglePostedFilter(option.value)
                                        }
                                      />
                                      <span>{option.label}</span>
                                    </label>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  className="table-filter-popup__reset"
                                  onClick={resetPostedFilter}
                                >
                                  {t('button.reset')}
                                </button>
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRows.length > 0 ? (
                    <>
                      {displayedRows.map((row, index: number) => {
                        const rec = row.rec

                        return (
                          <React.Fragment
                            key={`${rec.parent_id || 'row'}-${row.originalIndex}`}
                          >
                            <tr className="align-middle h-60">
                              <td className="text-start">{index + 1}</td>
                              <td className="text-center">
                                <strong>
                                  {row.transactionTimestamp
                                    ? moment.utc(row.transactionTimestamp).format(
                                        'MM-DD-YYYY'
                                      )
                                    : t('common.na')}
                                </strong>
                              </td>
                              <td className="text-center">{row.coaValue}</td>
                              <td
                                className="text-start text-truncate"
                                style={{ maxWidth: '110px' }}
                                title={rec.description || t('common.na')}
                              >
                                {rec.description || t('common.na')}
                              </td>
                              {transationType === 3 ? (
                                <>
                                  <td className="text-end">
                                    {formatCurrencyAmount(rec.deposit)}
                                  </td>
                                  <td className="text-end">
                                    {formatCurrencyAmount(rec.equity)}
                                  </td>
                                  <td className="text-end">
                                    {formatCurrencyAmount(rec.bank_transfer)}
                                  </td>
                                  <td className="text-end">
                                    {formatCurrencyAmount(rec.others)}
                                  </td>
                                  <td className="text-end">
                                    {formatCurrencyAmount(
                                      rec.adjusted_bank_deposit
                                    )}
                                  </td>
                                </>
                              ) : transationType === 1 ? (
                                <>
                                  <td className="text-end">
                                    {formatCurrencyAmount(rec.expense)}
                                  </td>
                                </>
                              ) : (
                                <></>
                              )}
                              <td className="text-center">{row.postedValue}</td>
                            </tr>
                          </React.Fragment>
                        )
                      })}
                    </>
                  ) : (
                    <tr>
                      <td
                        colSpan={transationType === 3 ? 10 : 6}
                        className="text-center"
                      >
                        {t('message.noData')}
                      </td>
                    </tr>
                  )}
                </tbody>
                {displayedRows.length > 0 ? (
                  <tfoot>
                    <tr className="align-middle h-60 table-sticky-total-row">
                      <td></td>
                      <td></td>
                      <td></td>
                      <td className="text-right">
                        <strong>{t('table.total')}</strong>
                      </td>
                      {transationType === 3 ? (
                        <>
                          <td className="text-end">
                            {formatCurrencyAmount(displayTotals.deposit)}
                          </td>
                          <td className="text-end">
                            {formatCurrencyAmount(displayTotals.equity)}
                          </td>
                          <td className="text-end">
                            {formatCurrencyAmount(displayTotals.bankTransfer)}
                          </td>
                          <td className="text-end">
                            {formatCurrencyAmount(displayTotals.others)}
                          </td>
                          <td className="text-end">
                            {formatCurrencyAmount(
                              displayTotals.adjustedBankDeposit
                            )}
                          </td>
                        </>
                      ) : transationType === 1 ? (
                        <>
                          <td className="text-end">
                            {formatCurrencyAmount(displayTotals.expense)}
                          </td>
                        </>
                      ) : (
                        <></>
                      )}
                      <td></td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
              <ScrollToggle scrollRef={tableContainerRef} />
            </div>

            {/* <Pagination currentPage={currentPage} setCurrentPage={setCurrentPage} recordPerPage={recordPerPage} lastIndex={lastIndex} firstIndex={firstIndex} npage={totalPages} fetchData={fetchTransactionList} /> */}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BankTransaction